import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { MyRoutes } from "../myRoutes/MyRoutes";
import { AppBreadcrumbs } from "../../common/breadcrumb/Breadcrumb";
import { Header } from "../header/Header";
// import {WhiteGlovesHeader} from "../whiteGlovesHeader/WhiteGlovesHeader";
// import {WhiteGlovesFooter} from "../whiteGlovesFooter/WhiteGlovesFooter";
import { useLocation } from "react-router";
import { useNavigate } from "react-router-dom";
import { setNavigate } from "../../common/services/navigationService";
import { MotifProgressLoader } from "@ey-xd/motif-react";
import { useIsAuthenticated } from "@azure/msal-react";
import {InactivityLogout} from"../layout/InactivityLogout";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import useLogin from "../../common/redux/common/login/useLogin";
import { PARTNER } from "../../common/contants/constants";
import logger from "../../common/utils/logger";
import { getPlannedOutageActiveRequest, getPlannedOutagePriorRequest } from "../partnerFileUpload/redux/partnerFileUploadSlice";
import usePartnerFileUpload from "../partnerFileUpload/hooks/usePartnerFileUpload";
import { ApiStatus } from "../../common/services/api-status.enum";
import { checkOutageWindow, configureOutageMonitor, resetOutageMonitor } from "../../common/services/outageMonitor";

const MAINTENANCE_PATH = "/maintenance";
// The chatbot's Privacy Notice page renders its own EY header bar (see
// privacyNoticePage.jsx), so the app-level Header is hidden there to avoid
// showing two headers stacked on top of each other.
const CHAT_PRIVACY_NOTICE_PATH = "/chat/privacy-notice";
// Routes open to every signed-in user, even without a DBS role.
const OPEN_TO_ALL_USERS_PATHS = ["/chat"];
const isOpenToAllUsersPath = (pathname) =>
  OPEN_TO_ALL_USERS_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

export const Layout = () => {
  const location = useLocation();
  const [userRole, setUserRole] = useState(null); // Initialize userRole as null
  const [isLoading, setIsLoading] = useState(true); // Initialize isLoading as true
  const [showRestrictedDialog, setShowRestrictedDialog] = useState(false); // New state for restricted dialog
  const [showTokenExpiryDialog, setShowTokenExpiryDialog] = useState(false); // New state for restricted dialog
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { plannedOutageActiveData, plannedOutagePriorData } = usePartnerFileUpload();
  const isAuthenticated = useIsAuthenticated();

  // Register global navigate reference once available
  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);
  const { loginDetail } = useLogin();

  // Outage monitoring/redirection is scoped to the Partner flow only. Admin
  // (and any other non-Partner role) must continue using the app during a
  // planned outage window, so we skip the whole outage pipeline for them.
  const isPartnerUser = userRole === PARTNER;

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    if (!isPartnerUser) {
      return;
    }
    dispatch(getPlannedOutagePriorRequest());
  }, [dispatch, isAuthenticated, isPartnerUser]);

  // Configure the outage monitor from the "prior" outage response so the axios
  // interceptor (which calls checkOutageWindow on every response) can fire the
  // active-outage request exactly once as soon as the announced start time is
  // reached.
  useEffect(() => {
    if (!isAuthenticated || !isPartnerUser) {
      resetOutageMonitor();
      return;
    }
    if (plannedOutagePriorData?.status !== ApiStatus.SUCCESS) return;

    const priorPayload = plannedOutagePriorData?.data;
    const startsUtc = priorPayload?.outageStartsUtc;

    configureOutageMonitor({
      startsUtc,
      onOutageStart: () => dispatch(getPlannedOutageActiveRequest()),
    });

    if (startsUtc) {
      // If the start time has already passed by the time the prior response
      // arrives, trigger immediately instead of waiting for the next API call.
      // When we are already on /maintenance, the outage page's mount effect
      // owns the active-outage dispatch, so avoid a duplicate call from here.
      if (location.pathname !== MAINTENANCE_PATH) {
        checkOutageWindow();
      }
    } else if (!priorPayload && location.pathname !== MAINTENANCE_PATH) {
      dispatch(getPlannedOutageActiveRequest());
    }
  }, [dispatch, isAuthenticated, isPartnerUser, plannedOutagePriorData, location.pathname]);

  // Redirect to /maintenance when an active outage is detected (unless the user
  // is flagged to bypass it), and keep them off the maintenance page otherwise.
  // Admin users are never redirected so their admin workflows are not interrupted.
  useEffect(() => {
    if (!isPartnerUser) return;
    if (plannedOutageActiveData?.status !== ApiStatus.SUCCESS) return;
    const data = plannedOutageActiveData?.data;
    const isDuringOutage = !!data?.isDuringOutage && !data?.isBypassUser;

    if (isDuringOutage && location.pathname !== MAINTENANCE_PATH) {
      navigate(MAINTENANCE_PATH, { replace: true });
    }
  }, [isPartnerUser, plannedOutageActiveData, location.pathname, navigate]);

  // Guard against manual navigation to /maintenance. The maintenance page is
  // only valid for Partner users during an active outage window. Anyone else
  // (Admin, unauthenticated, or Partner without an active outage) is redirected
  // to the login route. This effect is also the single source of the active
  // outage fetch on the /maintenance route so admin-side schedule updates are
  // reflected on refresh without duplicating calls made elsewhere.
  useEffect(() => {
    if (location.pathname !== MAINTENANCE_PATH) return;
    if (isLoading || userRole === null) return;

    if (!isAuthenticated || !isPartnerUser) {
      navigate("/", { replace: true });
      return;
    }

    const activeStatus = plannedOutageActiveData?.status;
    if (activeStatus !== ApiStatus.SUCCESS) {
      if (activeStatus !== ApiStatus.LOADING) {
        dispatch(getPlannedOutageActiveRequest());
      }
      return;
    }

    const data = plannedOutageActiveData?.data;
    const isDuringOutage = !!data?.isDuringOutage && !data?.isBypassUser;
    if (!isDuringOutage) {
      navigate("/", { replace: true });
    }
  }, [
    location.pathname,
    isLoading,
    userRole,
    isAuthenticated,
    isPartnerUser,
    plannedOutageActiveData,
    dispatch,
    navigate,
  ]);
  
  useEffect(() => {
    const getRole = async () => {
      try {       
          const fetchedRole = loginDetail?.data?.userRole === "" ? "User doesn't exsist" : loginDetail?.data?.userRole;
          const hasNoRole = loginDetail?.data?.userRole === "User doesn't exsist" || loginDetail?.data?.userRole === "";
          if (hasNoRole && !isOpenToAllUsersPath(location.pathname)) {
        setShowRestrictedDialog(true); // Show restricted dialog if role is User
          } else {
        setShowRestrictedDialog(false);
        setShowTokenExpiryDialog(false);
        }
          // Check if the role is 'User' and the URL contains '/steps' or '/settings'
          if (fetchedRole === "User doesn't exsist" && (location.pathname.includes('/steps') || location.pathname.includes('/settings') || location.pathname.includes('/uploadedFiles') || location.pathname.includes('/adminPage'))) {
        window.location.href = '/Login'; // Redirect using window.location
        return;
          }
          // Check if the role is 'Partner' and the URL contains '/settings'
          if (fetchedRole === PARTNER && (location.pathname.includes('/settings') || location.pathname.includes('/adminPage'))) {
        window.location.href = '/Login'; // Redirect using window.location
        return;
          }
          setUserRole(fetchedRole); // Set the user role
        
      } catch (error) {
        logger.error(error);
        setUserRole('User'); // Default to 'User' on error
      } finally {
        setIsLoading(false); // Set loading state to false
      }
    };

    getRole();
  }, [location.pathname, loginDetail]); // Dependency on location.pathname

  if (isLoading || userRole === null) {
    // Show loading indicator while loading or userRole is not determined
    return <MotifProgressLoader show variant="default" />;
  }

  // Render the layout content if not loading and userRole is determined
  return (
    <>
      <InactivityLogout /> 
          {location.pathname !== "/logout" && location.pathname !== "/session-expired" && location.pathname !== MAINTENANCE_PATH && !location.pathname.startsWith("/whitegloves") && location.pathname !== "/faq-viewer" && location.pathname !== CHAT_PRIVACY_NOTICE_PATH &&(
        <>
          <Header userRole={userRole} /> {/* Pass userRole as prop to Header */}
        </>
      )}
      {/* { location.pathname.startsWith("/whitegloves/") &&
        <WhiteGlovesHeader userRole={userRole}/>
      } */}
      {location.pathname !== "/" && location.pathname !== "/logout" && location.pathname !== "/session-expired" && location.pathname !== "/error" /* && !location.pathname.startsWith("/whitegloves") */ && (
        <>
          {/* {userRole !== PARTNER && <AppBreadcrumbs />} */}
        </>
      )}
        {/* {location.pathname.startsWith("/whitegloves")  && (
        <>
          { <AppBreadcrumbs />}
        </>
      )} */}

      <MyRoutes />
      {/* { location.pathname.startsWith("/whitegloves/") &&
        <WhiteGlovesFooter userRole={userRole}/>
      } */}
       <Dialog
                              open={showRestrictedDialog}
                              onClose={(event, reason) => {
                                if (reason !== 'backdropClick') {
                                  setShowRestrictedDialog(false);
                                }
                              }}
                              className="role-ristrict-dialog"
                          >
                              <DialogTitle>{"Access Restricted"}</DialogTitle>
                              <DialogContent>
                                  The role is currently restricted. Please contact the Data Bureau Service at <a href="mailto:DBS@parthenon.ey.com">DBS@parthenon.ey.com</a> for access.
                              </DialogContent>
                          </Dialog>
                          <Dialog
                              open={showTokenExpiryDialog}
                              onClose={(event, reason) => {
                                if (reason !== 'backdropClick') {
                                  setShowTokenExpiryDialog(false);
                                }
                              }}
                              className="role-ristrict-dialog"
                          >
                              <DialogTitle>{"Session Timed Out"}</DialogTitle>
                              <DialogContent>
                              The session is timed out, please start a new session to log in.
                              </DialogContent>
                          </Dialog>
    </>
  );
};